import * as moment from 'moment';
import { TipoReadaptacao } from '../../../../core/enums/tipo-readaptacao.enum';
import { TipoDocumentoReadaptacao } from '@core/enums/tipo-documento-readaptacao.enum';
import { EnumBaseType } from 'src/app/shared/types/enum-base.type';
import { UploadArquivoDTO } from './create-documentos-intervencao-readaptacao.dto';
import { AutocompleteDto } from '@core/dto/auto-complete-dto';
import { SituacaoDocumentoReadaptacao } from '@core/enums/situacao-documento-readaptacao.enum';

export class DadosDocumentosReadaptacaoDTO {
  public dadosCardsEPeriodo: DadosCardsEPeriodoReadaptacaoDTO[];
  public isCalendarioVigente: boolean;
  constructor(obj?: any) {
    if (obj) {
      this.dadosCardsEPeriodo = obj.dadosCardsEPeriodo.map((item: any) => new DadosCardsEPeriodoReadaptacaoDTO(item)) || [];
      this.isCalendarioVigente = obj.isCalendarioVigente || false;
    }
  }
}

export class DadosCardsEPeriodoReadaptacaoDTO {
  public dadosPeriodo: DadosPeriodoDTO;
  public dadosBimestres: DadosBimestresDTO[];

  constructor(obj?: any) {
    if (obj) {
      this.dadosPeriodo = new DadosPeriodoDTO(obj.dadosPeriodo);
      this.dadosBimestres = obj.dadosBimestres.map((item: any) => new DadosBimestresDTO(item)) || [];
    }
  }

}

export class DadosPeriodoDTO {
  dataInicio: Date;
  dataFim: Date;
  tipoReadaptacao: EnumBaseType;
  ano: number;
  versaoCalendario: string;
  totalBimestresPeriodo: number;

  dataFimFormatada: string;
  dataInicioFormatada: string;

  constructor(obj?: any) {
    if (!obj) return;

    this.inicializarDatas(obj);
    this.inicializarDadosBasicos(obj);
  }

  private inicializarDatas(obj: any): void {
    const { dataInicio, dataFim } = this.formatarDatas(obj.dataInicio, obj.dataFim);
    this.dataInicio = dataInicio.data;
    this.dataInicioFormatada = dataInicio.formatada;
    this.dataFim = dataFim.data;
    this.dataFimFormatada = dataFim.formatada;
  }

  private formatarDatas(dataInicio: any, dataFim: any) {
    return {
      dataInicio: this.formatarData(dataInicio),
      dataFim: this.formatarData(dataFim)
    };
  }

  private formatarData(data: any): { data: Date; formatada: string } {
    return {
      data: data ? new Date(data) : null,
      formatada: data ? moment(data).format('DD/MM/YYYY') : ''
    };
  }

  private inicializarDadosBasicos(obj: any): void {
    this.tipoReadaptacao = obj.tipoReadaptacao ? new TipoReadaptacao().getById(obj.tipoReadaptacao) : null;
    this.ano = obj.ano || '';
    this.versaoCalendario = obj.versaoCalendario || '';
    this.totalBimestresPeriodo = obj.totalBimestresPeriodo || 0;
  }
}

export class DadosBimestresDTO {
  public id?: string;
  public tipoDocumento: EnumBaseType;
  public bimestre: number | null;
  public isDocumentoAnexado: boolean;
  public ano: number;
  public dataInicio: Date;
  public dataFim: Date;

  public arquivo: UploadArquivoDTO;
  public dimensoesProjetoIds: AutocompleteDto[];
  public comentario: string;
  public dataUpload: Date;
  public dataUploadFormatada: string;
  public situacao: EnumBaseType;

  public dataInicioFormatada: string;
  public dataFimFormatada: string;
  public tituloCard: string;
  public label1Card: string;
  public label2Card: string;

  // Estratégias para definição de textos do card
  private readonly estrategiasCard = {
    'PROJETO_PEDAGOGICO': () => ({
      tituloCard: 'Projeto de Intervenção Pedagógica',
      label1Card: 'Documento anual obrigatório'
    }),
    'RELATORIO_EXECUCAO': () => ({
      tituloCard: `${this.bimestre}º Bimestre - ${this.ano}`,
      label1Card: 'Relatório de Execução'
    })
  };

  constructor(obj?: any) {
    if (!obj) return;

    this.inicializarCamposBasicos(obj);
    this.inicializarDatas(obj);
    this.inicializarDocumento(obj);
    this.definirTextosCard();
  }

  private inicializarCamposBasicos(obj: any): void {
    this.id = obj.id || null;
    this.bimestre = obj.bimestre || null;
    this.isDocumentoAnexado = obj.isDocumentoAnexado || false;
    this.ano = obj.ano || '';
  }

  private inicializarDatas(obj: any): void {
    const periodoFormatado = this.formatarPeriodoDatas(obj.dataInicio, obj.dataFim);
    this.dataInicio = periodoFormatado.dataInicio.data;
    this.dataInicioFormatada = periodoFormatado.dataInicio.formatada;
    this.dataFim = periodoFormatado.dataFim.data;
    this.dataFimFormatada = periodoFormatado.dataFim.formatada;

    const uploadFormatado = this.formatarDataUpload(obj.dataUpload);
    this.dataUpload = uploadFormatado.data;
    this.dataUploadFormatada = uploadFormatado.formatada;
  }

  private formatarPeriodoDatas(dataInicio: any, dataFim: any) {
    return {
      dataInicio: this.formatarData(dataInicio, 'DD/MM/YYYY'),
      dataFim: this.formatarData(dataFim, 'DD/MM/YYYY')
    };
  }

  private formatarData(data: any, formato: string): { data: Date; formatada: string } {
    return {
      data: data ? new Date(data) : null,
      formatada: data ? moment(data).format(formato) : ''
    };
  }

  private formatarDataUpload(dataUpload: any): { data: Date; formatada: string } {
    return this.formatarData(dataUpload, 'DD/MM/YYYY HH:mm');
  }

  private inicializarDocumento(obj: any): void {
    this.tipoDocumento = this.parsearTipoDocumento(obj);
    this.arquivo = obj.arquivo ? new UploadArquivoDTO(obj.arquivo) : null;
    this.dimensoesProjetoIds = obj.dimensoesProjetoIds || [];
    this.comentario = obj.comentario || '';
    this.situacao = this.parsearSituacao(obj);
  }

  private parsearTipoDocumento(obj: any): EnumBaseType {
    const valorTipoDocumento = obj?.tipoDocumento?.value || obj.tipoDocumento || '';
    return new TipoDocumentoReadaptacao().getById(valorTipoDocumento);
  }

  private parsearSituacao(obj: any): EnumBaseType {
    const valorSituacao = obj.situacao?.value || obj.situacao || '';
    return new SituacaoDocumentoReadaptacao().getById(valorSituacao);
  }

  private definirTextosCard(): void {
    if (!this.tipoDocumento) return;

    this.inicializarLabelsDefault();
    this.aplicarEstrategiaCard();
  }

  private inicializarLabelsDefault(): void {
    this.tituloCard = '';
    this.label1Card = '';
    this.label2Card = this.isDocumentoAnexado ? '1 documento' : '0 documento - pendente';
  }

  private aplicarEstrategiaCard(): void {
    const tipoDocumentoKey = this.obterChaveTipoDocumento();
    const estrategia = this.estrategiasCard[tipoDocumentoKey];

    if (estrategia && this.validarExecucaoEstrategia(tipoDocumentoKey)) {
      const textos = estrategia();
      this.tituloCard = textos.tituloCard;
      this.label1Card = textos.label1Card;
    }
  }

  private obterChaveTipoDocumento(): string {
    const tipoProjetoPedagogico = new TipoDocumentoReadaptacao().getById('PROJETO_PEDAGOGICO');
    const tipoRelatorioExecucao = new TipoDocumentoReadaptacao().getById('RELATORIO_EXECUCAO');

    if (this.tipoDocumento.value === tipoProjetoPedagogico.value) {
      return 'PROJETO_PEDAGOGICO';
    }
    if (this.tipoDocumento.value === tipoRelatorioExecucao.value) {
      return 'RELATORIO_EXECUCAO';
    }
    return '';
  }

  private validarExecucaoEstrategia(tipoDocumentoKey: string): boolean {
    if (tipoDocumentoKey === 'RELATORIO_EXECUCAO') {
      return !!this.bimestre;
    }
    return true;
  }
}
